// Types
export type * from './types/game.js';
export type { GameMode, VisualConfig } from './types/game.js';

// Utility functions
export * from './utils/gameLogic.js';

// Hooks/Managers
export * from './hooks/useKeyboard.js';
export * from './hooks/useGameLoop.js';
export * from './hooks/useGameState.js';

// Atomic components
export * from './components/atoms/Ball.js';
export * from './components/atoms/Paddle.js';
export * from './components/atoms/ScoreDisplay.js';

// Import types and classes
import type { GameConfig, GameCallbacks, GameState } from './types/game.js';
import { GameStateManager } from './hooks/useGameState.js';
import { Ball } from './components/atoms/Ball.js';
import { Paddle } from './components/atoms/Paddle.js';
import { ScoreDisplay } from './components/atoms/ScoreDisplay.js';

// Main game class for easy instantiation
export class PingPong {
  private gameStateManager: GameStateManager;
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private ball!: Ball;
  private playerPaddle!: Paddle;
  private aiPaddle!: Paddle;
  private scoreDisplay!: ScoreDisplay;
  private animationId: number | null = null;

  constructor(
    canvas: HTMLCanvasElement, 
    config?: Partial<GameConfig>,
    callbacks?: GameCallbacks
  ) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to get 2D context from canvas');
    }
    this.context = context;
    
    // Initialize game state manager
    this.gameStateManager = new GameStateManager(config, callbacks);
    
    // Initialize components
    this.initializeComponents();
    
    // Set up state listener
    this.gameStateManager.addListener(this.handleStateChange.bind(this));
    
    // Start render loop
    this.startRenderLoop();
  }

  private initializeComponents(): void {
    const state = this.gameStateManager.getState();
    const config = this.gameStateManager.getConfig();
    
    // Initialize ball
    this.ball = new Ball({
      ball: state.ball,
      context: this.context,
      color: config.visual.colors.ball
    });
    
    // Initialize player paddle
    this.playerPaddle = new Paddle({
      paddle: state.playerPaddle,
      context: this.context,
      color: config.visual.colors.playerPaddle
    });
    
    // Initialize AI paddle
    this.aiPaddle = new Paddle({
      paddle: state.aiPaddle,
      context: this.context,
      color: config.visual.colors.aiPaddle
    });
    
    // Initialize score display
    this.scoreDisplay = new ScoreDisplay({
      score: state.score,
      context: this.context,
      position: { x: 10, y: 20 },
      color: config.visual.colors.text,
      font: config.visual.ui.font,
      fontSize: 16
    });
  }

  private handleStateChange(state: GameState): void {
    const config = this.gameStateManager.getConfig();
    
    // Update component props with new state and colors
    this.ball.updateProps({ 
      ball: state.ball,
      color: config.visual.colors.ball 
    });
    this.playerPaddle.updateProps({ 
      paddle: state.playerPaddle,
      color: config.visual.colors.playerPaddle 
    });
    this.aiPaddle.updateProps({ 
      paddle: state.aiPaddle,
      color: config.visual.colors.aiPaddle 
    });
    this.scoreDisplay.updateProps({ 
      score: state.score,
      color: config.visual.colors.text,
      font: config.visual.ui.font,
      fontSize: 16
    });
  }

  private startRenderLoop(): void {
    const render = () => {
      this.render();
      this.animationId = requestAnimationFrame(render);
    };
    render();
  }

  private render(): void {
    const config = this.gameStateManager.getConfig();
    
    // Clear canvas with configurable background color
    this.context.fillStyle = config.visual.colors.background;
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Draw center line (if enabled)
    if (config.visual.ui.showCenterLine) {
      this.drawCenterLine();
    }
    
    // Render game components
    this.scoreDisplay.render();
    this.ball.render();
    this.playerPaddle.render();
    this.aiPaddle.render();
    
    // Draw game state messages (if enabled)
    if (config.visual.ui.showOverlayText) {
      this.drawGameStateMessages();
    }
  }

  private drawCenterLine(): void {
    const config = this.gameStateManager.getConfig();
    const lineWidth = 4;
    const segmentHeight = config.canvas.height / 20;
    
    this.context.fillStyle = config.visual.colors.centerLine;
    
    for (let y = 0; y < config.canvas.height; y += segmentHeight) {
      this.context.fillRect(
        (config.canvas.width - lineWidth) / 2,
        y + segmentHeight * 0.25,
        lineWidth,
        segmentHeight * 0.5
      );
    }
  }

  private drawGameStateMessages(): void {
    const state = this.gameStateManager.getState();
    const config = this.gameStateManager.getConfig();
    
    this.context.fillStyle = config.visual.colors.text;
    this.context.font = `${config.visual.ui.fontSize}px ${config.visual.ui.font}`;
    this.context.textAlign = 'center';
    
    const centerX = config.canvas.width / 2;
    const centerY = config.canvas.height / 2;
    
    switch (state.gamePhase) {
      case 'waiting':
        this.context.fillText('Press SPACE to start', centerX, centerY);
        break;
      case 'scored':
        const scorer = state.score.player > state.score.ai ? 'Player' : 'CPU';
        this.context.fillText(`${scorer} scored!`, centerX, centerY);
        break;
      case 'gameOver':
        const winner = state.winner === 'player' ? 'Player' : 'CPU';
        this.context.fillText(`${winner} wins!`, centerX, centerY);
        break;
    }
  }

  // Public API methods
  public start(): void {
    this.gameStateManager.startGame();
  }

  public pause(): void {
    this.gameStateManager.pauseGame();
  }

  public resume(): void {
    this.gameStateManager.resumeGame();
  }

  public stop(): void {
    this.gameStateManager.stopGame();
  }

  public reset(): void {
    this.gameStateManager.resetGame();
  }

  public getState(): GameState {
    return this.gameStateManager.getState();
  }

  public updateConfig(config: Partial<GameConfig>): void {
    this.gameStateManager.updateConfig(config);
  }

  public toggleGameMode(): void {
    this.gameStateManager.toggleGameMode();
  }

  public cleanup(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    this.gameStateManager.cleanup();
  }
}

// Default export
export default PingPong; 